import React from 'react';
import ToasterContextProvider from '@app/component/overlays/toaster/context/ToasterContextProvider';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import SignInPage from '@app/site/authentication/page/SignInPage';
import SignUpPage from '@app/site/registration/page/SignUpPage';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ProfileVerificationFlowPage from '@app/site/registration/flow/ProfileVerificationFlowPage';
import RegistrationFlowPage from '@app/site/registration/page/RegistrationFlowPage';
import VerifyProfilePage from '@app/site/registration/page/VerifyProfilePage';
import ResetPasswordRequestPage from '@app/site/profile-reset-password/page/ResetPasswordRequestPage';
import ResetPasswordPage from '@app/site/profile-reset-password/page/ResetPasswordPage';
import ErrorPageNotFound from '@app/site/system/page/ErrorPageNotFound';
import useSecurity from '@util/SecurityHook';
import ProtectedRoute from '@app/site/ProtectedRoute';
import ErrorPageAccessDenied from '@app/site/system/page/ErrorPageAccessDenied';
import AcceptCompanyProfileInvitationPage from '@app/site/registration/page/AcceptCompanyProfileInvitationPage';
import { GoogleOAuthProvider } from '@react-oauth/google';
import process from 'process';
import AfterAuthenticationHandler from '@app/site/authentication/page/AfterAuthenticationHandler';
import WebHotelierPage from '@app/site/integration/webhotelier/page/WebHotelierPage';
import { MixpanelProvider } from '@app/site/mixpanel/MixpanelProvider';
import { WebHotelierWelcome1 } from '@app/site/integration/webhotelier/page/WebHotelierWelcome1';
import { WebHotelierErrorPage } from '@app/site/integration/webhotelier/page/WebHotelierErrorPage';
import { WebHotelierWelcome2 } from '@app/site/integration/webhotelier/page/WebHotelierWelcome2';
import { WebHotelierWelcome3 } from '@app/site/integration/webhotelier/page/WebHotelierWelcome3';
import ParityUserCreateCompanyHotelPage from '@app/site/registration/flow/ParityUserCreateCompanyHotelPage';
import DomainRouter from '@app/site/secured/DomainRouter';
import MarketStateProofPage
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/proof/MarketStateProofPage';

const AuthenticationRedirect = React.memo(() => {
	const security = useSecurity();

	return security.isAuthenticated() ?
		<Navigate to={'/secured'} replace /> :
		<Navigate to={'/sign-in'} replace />;
});

const priceSubmitters: readonly unknown[] = [
	PricingAutomationService.submitPricing,
	PricingAutomationService.submitManualPricing,
	PricingAutomationService.submitMonthlyPricing,
	PricingAutomationService.submitSelectivePricing,
	PricingAutomationService.submitBulkManualPricing,
];

const queryClient = new QueryClient({
	mutationCache: new MutationCache({
		onSuccess: (_data, variables, _context, mutation) => {
			const companyHotelId = (variables as { companyHotelId?: number } | undefined)?.companyHotelId;
			if (typeof companyHotelId === 'number' && priceSubmitters.includes(mutation.options.mutationFn)) {
				void queryClient.invalidateQueries({ queryKey: PricingAutomationQueryKeys.pricingSubmissions(companyHotelId) });
				void queryClient.invalidateQueries({ queryKey: PricingAutomationQueryKeys.pmsMetrics(companyHotelId) });
			}
		},
	}),
});

const AppRouter = React.memo(() => {

	const MIX_PANEL_TOKEN = 'b1b1acd50d7c4aaec4d485501b02267b';
	const MIX_PANEL_CONFIG = {
		autocapture: true,
		record_sessions_percent: 100,
		record_mask_text_selector: '',
	};

	return <QueryClientProvider client={queryClient}>
		<ToasterContextProvider>
			<GoogleOAuthProvider clientId={process.env['REACT_APP_GOOGLE_CLIENT_ID']!}>
				<MixpanelProvider config={MIX_PANEL_CONFIG} token={MIX_PANEL_TOKEN} name="Mixpanel">
					<BrowserRouter>
						<Routes>
							<Route path="*" element={<ErrorPageNotFound />} />
							<Route path="access-denied" element={<ErrorPageAccessDenied />} />
							<Route path="/" element={<AuthenticationRedirect />}>
								<Route index element={<span>Index</span>} />
							</Route>
							<Route path="/sign-in" element={<SignInPage />} />
							<Route path="/sign-up" element={<SignUpPage />} />
							<Route path="/webhotelier-welcome-1" element={<WebHotelierWelcome1 />} />
							<Route path="/webhotelier-welcome-2" element={<WebHotelierWelcome2 />} />
							<Route path="/webhotelier-welcome-3" element={<WebHotelierWelcome3 />} />
							<Route path="/webhotelier-help" element={<WebHotelierErrorPage />} />
							<Route path="/oauth2/redirect" element={<AfterAuthenticationHandler />}></Route>
							<Route path="/verification-pending" element={<ProfileVerificationFlowPage />} />
							<Route path="/registration" element={<RegistrationFlowPage />} />
							<Route path="/registration/create-parity-company-hotel"
							       element={<ParityUserCreateCompanyHotelPage />} />
							<Route path="/verify-profile/:key" element={<VerifyProfilePage />} />
							<Route path="/accept-invitation/:key" element={<AcceptCompanyProfileInvitationPage />} />
							<Route path="/forgot-password" element={<ResetPasswordRequestPage />} />
							<Route path="/reset-password/:key" element={<ResetPasswordPage />} />
							<Route path="/webhotelier/callback" element={<WebHotelierPage />} />
							<Route path="/secured" element={<ProtectedRoute />}>
								<Route path="proof/:companyHotelId/choice/:proofChoiceId" element={<MarketStateProofPage />} />
								<Route index element={<Navigate to="/secured/dashboard" />} />
								<Route path="*" element={<DomainRouter />} />
							</Route>

						</Routes>
					</BrowserRouter>
				</MixpanelProvider>
			</GoogleOAuthProvider>
		</ToasterContextProvider>
		{<ReactQueryDevtools initialIsOpen={false} />}
	</QueryClientProvider>;
});

export default AppRouter;
